import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest } from 'rxjs';
import { callback } from 'chart.js/dist/helpers/helpers.core';
import { DecimalPipe } from '@angular/common';
import { UtilsService } from '../../../../../core/services/utils.service';
@Component({
  selector: 'app-cost-break-down-chart',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './cost-break-down-chart.component.html',
  styleUrl: './cost-break-down-chart.component.css'
})
export class CostBreakDownChartComponent implements OnInit, OnChanges {
  data: any;
  options: any;

  // Dummy data
  @Input() showDummyData: boolean = false;
  dummyshowData: number[] = [0.28, 0.07, 0.25, 0.31, 0.09]
  dummytotalPricePerUnit: number = 2.5;

  label: string[] = ['Material Costs', 'Dispatch', 'TMS', 'Taxes', 'Additional Charges'];
  @Input() showData: number[] = [0.28, 0.07, 0.25, 0.31, 0.09]
  @Input() totalPricePerUnit: number = 2.5;
  backgroundColors: string[] = ['#749256', '#A4BD8E', '#3F502A', '#B9D1A1', '#5A713E'];

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe, private utilsService: UtilsService) {

  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateChart();
  }

  ngOnInit(): void {
    this.updateChart();

    const labelKeys = [
      'dashboard.cost_break_down_labels.material_costs',
      'dashboard.cost_break_down_labels.dispatch',
      'dashboard.cost_break_down_labels.tms',
      'dashboard.cost_break_down_labels.taxes',
      'dashboard.cost_break_down_labels.additional_charges'
    ];

    combineLatest(labelKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.label = translations;
      this.updateChart();
    });
  }


  updateChart(): void {
    if (this.showDummyData) {
      this.showData = this.dummyshowData;
      this.totalPricePerUnit = this.dummytotalPricePerUnit;
    }

    Chart.register(ChartDataLabels);

    const centerTextPlugin = {
      id: 'centerTextCB',
      beforeDraw(chart: any) {
        const pluginOptions = chart.config.options.plugins?.centerTextCB;
        if (!pluginOptions || pluginOptions.enabled === false) {
          return;
        }

        const ctx = chart.ctx;

        const { totalPricePerUnit = 0 } = pluginOptions;
        const { top, bottom, left, right } = chart.chartArea;
        const centerX = (left + right) / 2;
        const centerY = (top + bottom) / 2;

        const text = (totalPricePerUnit == 0) ? '' : `${totalPricePerUnit}\n€/kWh`;

        ctx.save();
        ctx.font = 'bold 20px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        text.split('\n').forEach((line, index) => {
          ctx.fillText(line, centerX, centerY + index * 20);
        });

        ctx.restore();
      },
    };
    Chart.register(centerTextPlugin);

    this.data = {
      labels: this.label,
      datasets: [
        {
          data: this.showData,
          backgroundColor: this.backgroundColors,
          hoverBackgroundColor: this.backgroundColors,
          borderWidth: 0,
          borderRadius: 6,
          spacing: 2,
        },
      ],
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '65%',
      plugins: {
        centerTextCB: {
          enabled: true,
          totalPricePerUnit: this.decimalPipe.transform(this.totalPricePerUnit, '1.2-2') || this.totalPricePerUnit,
        },
        legend: {
          display: false,
        },
        datalabels: {
          color: '#000',
          font: {
            size: 12,
            weight: 'normal',
            family: 'Gilroy',
          },
          formatter: (value: number, context: any) => {
            if (value === 0) {
              return '';
            }

            const dataset = context.chart.data.datasets[context.datasetIndex].data;
            const total = dataset.reduce((sum: number, val: number) => sum + val, 0);
            const percentage = (value / total) * 100;

            const label = context.chart.data.labels[context.dataIndex] || "";

            const wrappedLines = this.utilsService.getMultiLineLabels(label, 10);
            const percentText = `${this.decimalPipe.transform(percentage, '1.1-1')}%`;

            return [...wrappedLines, percentText];
          },
          align: 'end',
          anchor: 'end',
          padding: 10,
          clamp: true,
          display: true
        },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (tooltipItem: any) => {
              const dataset = tooltipItem.chart.data.datasets[tooltipItem.datasetIndex].data;
              const total = dataset.reduce((sum: number, val: number) => sum + val, 0);
              const value = tooltipItem.raw;
              const percentage = (value / total) * 100;

              return `${this.decimalPipe.transform(percentage, '1.1-1')}%`;
            }
          }
        },
      },
      layout: {
        padding: 80,
      },
    };
  }
}
